/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import Imgix from 'js/components/Imgix';
import { useLocation } from 'js/lib/useRouter';

import { Button, Skeleton, Typography2 } from '@coursera/cds-core';
import type { ButtonProps } from '@coursera/cds-core';
import type { ButtonName, SectionName } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { BROWSE_IN_SEARCH_PARTNERS_INDEX_ID } from 'bundles/search-common/constants';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchFacetValueAndCounts, SearchHit, SearchResult } from 'bundles/search-common/providers/searchTypes';
import { isInFilterTestVariant } from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import { scrollDownIfNecessary } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

const MAX_PARTNER_COUNT = 8;
const MIN_PARTNER_COUNT = 2;

type PartnerProp = {
  partner: PartnerItem;
  skillName: string;
  parnerIndex: number;
  handlePartnerClick: (partnerName: string) => void;
};

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

const styles = {
  partnerSectionContainer: css`
    margin-top: var(--cds-spacing-400);
    margin-bottom: var(--cds-spacing-400);
  `,
  partnerSectionTitle: css`
    margin-bottom: var(--cds-spacing-50);
  `,
  partnerList: css`
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto !important;
    gap: var(--cds-spacing-50);
    align-items: stretch;

    /* a11y when tabbing need padding */
    padding: var(--cds-spacing-50) var(--cds-spacing-100);
    margin: 0 -16px;

    /* Remove default list styles */
    list-style: none;
  `,
  partnerCardWrapper: css`
    /* needed to make sure all partnerCards have same height */
    flex: 1;
    width: 208px;
  `,
  partnerCard: css`
    width: 100%;
    max-width: unset !important;
    padding: 8px !important;
    display: flex;
    min-height: 100px;
    height: 100%;
    cursor: pointer;
    border-radius: 10px;
    flex: 1;
    justify-content: flex-start;

    .cds-button-label {
      display: flex;
      flex-direction: column;
      text-align: left;
      height: 100%;
      cursor: pointer;
    }
  `,
  partnerLogo: css`
    padding: var(--cds-spacing-200);
    width: 100%;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 10px;
    display: flex;
    justify-content: center;
  `,
  partnerName: css`
    padding: var(--cds-spacing-150) 0 0;
    width: 100%;
  `,
  partnerCount: css`
    width: 100%;
  `,
};
type PartnerItem = SearchFacetValueAndCounts & { logo: string };

export const PartnerSectionLoading = () => (
  <div css={styles.partnerSectionContainer} data-testid="partner-section-loading">
    <Skeleton height={24} variant="text" width="75%" css={styles.partnerSectionTitle} />
    <div css={styles.partnerList}>
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={`loading-card-${index}`} css={styles.partnerCard} data-testid="skeleton-card">
          <div className="cds-button-label" css={{ width: '100%' }}>
            <div css={styles.partnerLogo}>
              <Skeleton variant="rect" width={80} height={80} css={{ alignSelf: 'center' }} />
            </div>
            <Skeleton height={24} variant="text" width="100%" />
            <Skeleton height={24} variant="text" width="50%" />
            <Skeleton height={24} variant="text" width="25%" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const collectUniqueSortedPartners = (
  elements: SearchHit[],
  partnerMap: Record<string, SearchFacetValueAndCounts>
): PartnerItem[] => {
  const uniquePartners = new Map<string, PartnerItem>();

  for (const element of elements) {
    if (element.__typename === 'Search_ProductHit' && Array.isArray(element.partners)) {
      for (const [index, name] of element.partners.entries()) {
        if (name && !uniquePartners.has(name)) {
          const partner = partnerMap[name];
          if (partner && element.partnerLogos?.[index]) {
            uniquePartners.set(name, { ...partner, logo: element.partnerLogos?.[index] });
            if (uniquePartners.size >= MAX_PARTNER_COUNT) break;
          }
        }
      }
      if (uniquePartners.size >= MAX_PARTNER_COUNT) break;
    }
  }

  return Array.from(uniquePartners.values()).sort((a, b) => b.count - a.count);
};

const getPartners = (searchData?: SearchResult): PartnerItem[] => {
  if (!searchData) return [];
  const { elements, facets } = searchData;
  if (!elements || !facets) return [];

  const partnerFacet = facets.find((facet) => facet.name === 'partners');
  if (!partnerFacet) return [];

  const partnerMap = partnerFacet.valuesAndCounts.reduce<Record<string, SearchFacetValueAndCounts>>((acc, p) => {
    acc[p.value] = p;
    return acc;
  }, {});

  return collectUniqueSortedPartners(elements, partnerMap);
};

const PartnerCard = ({ partner, skillName, parnerIndex, handlePartnerClick }: PartnerProp) => {
  const track = useTracker();
  const partnerCardTrackingData = {
    partnerName: partner.value,
    partnerCardIndex: parnerIndex,
  };
  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_partner_card',
    partnerCardTrackingData,
    [],
    { fullyVisible: true }
  );

  const onClickToTrack = (partnerName: string) => {
    track('click_partner_card', partnerCardTrackingData);

    track('apply_filter', {
      filter: [`partners:${partnerName}`],
      filterSource: 'browse_in_serp',
    });
  };
  return (
    <li css={styles.partnerCardWrapper}>
      <TrackedButton
        _refAltAsTrueRef={visibilityTrackingRef}
        trackingName="partner_card"
        trackingData={{ skillName, partnerCount: partner.count, partner: partner.value }}
        css={styles.partnerCard}
        onClick={() => {
          onClickToTrack(partner.value);
          handlePartnerClick(partner.value);
        }}
        variant="ghost"
      >
        <div css={styles.partnerLogo} aria-hidden={true}>
          <Imgix
            width={64}
            height={64}
            src={partner.logo}
            alt={_t(`#{partnerName} logo`, { partnerName: partner.value })}
            degradeForSsr={true}
          />
        </div>
        <Typography2 component="p" variant="actionPrimary" css={styles.partnerName}>
          {partner.value}
        </Typography2>
        <Typography2 component="p" variant="actionSecondary" color="supportText" css={styles.partnerCount}>
          {_t('#{partnerCount} items', { partnerCount: partner.count })}
        </Typography2>
      </TrackedButton>
    </li>
  );
};

const PartnerSection = ({ skillName }: { skillName: string }) => {
  const {
    query: { language },
  } = useLocation();
  const isConsumerSerp = useIsConsumerSerp();
  const { getSearchResults, isLoading, setFiltersAndSortBy } = useSearch();
  const searchData = useMemo(() => getSearchResults(BROWSE_IN_SEARCH_PARTNERS_INDEX_ID), [getSearchResults]);
  const [partners, setPartners] = useState<PartnerItem[]>(getPartners(searchData) || []); // Persist state to maintain UI during loading

  useEffect(() => {
    if (!isLoading) setPartners(getPartners(searchData));
  }, [isLoading, searchData]);

  if (isLoading && partners.length < MIN_PARTNER_COUNT) {
    return <PartnerSectionLoading />;
  }
  if (!partners || partners.length < MIN_PARTNER_COUNT) {
    return null;
  }

  const handlePartnerClick = (partnerName: string) => {
    const hasEnglishLanguageInQuery = typeof language === 'string' && language === 'English';
    const hasLanguageFilter = isInFilterTestVariant() && hasEnglishLanguageInQuery;
    scrollDownIfNecessary({
      prefersReducedMotion: !!window?.matchMedia(`(prefers-reduced-motion: reduce)`)?.matches === true,
      mustBeScrolledPastThisElementId: 'search-results-header-wrapper',
    });
    if (hasLanguageFilter && isConsumerSerp) {
      setFiltersAndSortBy(searchData?.sortBy || 'BEST_MATCH', [[`partners:${partnerName}`], [`language:English`]]);
    } else {
      setFiltersAndSortBy(searchData?.sortBy || 'BEST_MATCH', [[`partners:${partnerName}`]]);
    }
  };

  return (
    <section css={styles.partnerSectionContainer}>
      <Typography2 component="h2" variant="subtitleLarge" css={styles.partnerSectionTitle}>
        {_t('Learn #{skillName} from leading universities and companies', { skillName })}
      </Typography2>
      <ul css={styles.partnerList}>
        {partners.map((partner, index) => {
          return (
            <PartnerCard
              parnerIndex={index}
              handlePartnerClick={handlePartnerClick}
              skillName={skillName}
              key={partner.value}
              partner={partner}
            />
          );
        })}
      </ul>
    </section>
  );
};

export default PartnerSection;
